import type { DailyCount, DailyMinutes } from '@dyspatch/core';
import type { ReactNode } from 'react';
import { bandPath, dayLabel, hasCounts, linePath, niceMax, scale } from '@/lib/charts';

/**
 * The metrics page's charts: bars and a line with a band, both inline SVG.
 *
 * No charting library, on purpose: this console has none as a dependency, and
 * `bun run build`'s bundle is watched (CLAUDE.md). Every shape here is small
 * enough to draw by hand, and drawing it by hand means the geometry is exactly
 * the four kinds `packages/hub/web/src/lib/charts.ts` computes and nothing a
 * library's defaults quietly added - a gradient, an animation on mount, a
 * colour that is not one of this page's own CSS variables.
 *
 * Every colour is a `var(--dy-*)` custom property, so a chart drawn under the
 * dark theme is the dark theme's own palette, not a light-mode chart with a
 * filter over it. Every entrance uses the page's existing `.dy-rise` class,
 * which `index.css` already turns off under `prefers-reduced-motion` - one
 * animation rule, not a second one invented for charts.
 */

const WIDTH = 560;
const HEIGHT = 132;
const AXIS_GAP = 18;

function Frame({
  children,
  label,
  width = WIDTH,
  height = HEIGHT,
}: {
  children: ReactNode;
  label: string;
  width?: number;
  height?: number;
}) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="dy-rise block w-full"
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      {children}
    </svg>
  );
}

/** What a chart draws instead of an empty axis when its window has nothing in it. */
function ChartEmpty({ text }: { text: string }) {
  return (
    <div
      className="text-fg-3 flex items-center justify-center text-center text-2xs"
      style={{ height: HEIGHT }}
    >
      {text}
    </div>
  );
}

function XAxisLabels({
  days,
  count,
  width = WIDTH,
  y = HEIGHT - 2,
}: {
  days: readonly string[];
  count: number;
  width?: number;
  y?: number;
}) {
  // At most five labels: the first, the last, and up to three evenly spaced
  // between them, so a 90-day window does not print ninety overlapping dates.
  const step = Math.max(1, Math.ceil((days.length - 1) / 4));
  const indices = days.map((_, i) => i).filter((i) => i % step === 0 || i === days.length - 1);
  return (
    <>
      {indices.map((i) => (
        <text
          key={days[i]}
          x={(i / Math.max(1, days.length - 1)) * width}
          y={y}
          fontSize="9"
          textAnchor={i === 0 ? 'start' : i === days.length - 1 ? 'end' : 'middle'}
          fill="var(--dy-fg-3)"
        >
          {dayLabel(days[i]!, count)}
        </text>
      ))}
    </>
  );
}

interface BarSeriesSpec {
  label: string;
  color: string;
  points: readonly DailyCount[];
}

/**
 * Grouped bars, one group per day. Used for throughput (merged, filed), for
 * retries/failures beside it, and for spend - the same shape, different series.
 *
 * `format` exists for that third caller. A `DailyCount` whose `count` is
 * nanodollars is still a bar of the right height, but "1500000000 spend" is not
 * a tooltip anybody can read, so the caller that knows what the number means
 * says how to print it rather than this file learning about money.
 */
export function BarChart({
  series,
  label,
  format,
}: {
  series: readonly BarSeriesSpec[];
  label: string;
  format?: (count: number) => string;
}) {
  const points = series[0]?.points ?? [];
  if (!points.length || !series.some((s) => hasCounts(s.points))) {
    return <ChartEmpty text={`Nothing to chart yet: ${label.toLowerCase()}.`} />;
  }
  const innerHeight = HEIGHT - AXIS_GAP;
  const max = niceMax(...series.map((s) => s.points.map((p) => p.count)));
  const groupWidth = WIDTH / points.length;
  const barWidth = Math.max(2, (groupWidth - 4) / series.length);
  const days = points.map((p) => p.since);
  return (
    <Frame label={label}>
      <line
        x1={0}
        y1={innerHeight}
        x2={WIDTH}
        y2={innerHeight}
        stroke="var(--dy-line)"
        strokeWidth={1}
      />
      {series.map((s, si) =>
        s.points.map((p, i) => {
          if (p.count <= 0) return null;
          const h = scale(p.count, max, innerHeight);
          const x = i * groupWidth + si * barWidth + 2;
          return (
            <rect
              key={`${s.label}-${p.since}`}
              x={x}
              y={innerHeight - h}
              width={Math.max(1, barWidth - 1)}
              height={h}
              fill={s.color}
              rx={1}
            >
              <title>
                {dayLabel(p.since, points.length)}:{' '}
                {format ? format(p.count) : `${p.count} ${s.label.toLowerCase()}`}
              </title>
            </rect>
          );
        }),
      )}
      <XAxisLabels days={days} count={points.length} />
    </Frame>
  );
}

/** A named colour swatch under a chart, so a reader knows which series is which. */
export function ChartLegend({ items }: { items: readonly { label: string; color: string }[] }) {
  return (
    <div className="text-fg-3 mt-1.5 flex flex-wrap gap-x-3 gap-y-1 px-1 text-2xs">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block size-2 rounded-full"
            style={{ background: item.color }}
            aria-hidden
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * A day's median as a line, its p90 as the band above it. `count` of zero on a
 * day means nothing was measured that day - a gap, not a duration of zero.
 */
export function LeadTimeChart({
  points,
  label,
  color,
}: {
  points: readonly DailyMinutes[];
  label: string;
  color: string;
}) {
  if (!hasCounts(points)) {
    return <ChartEmpty text={`Nothing measured yet: ${label.toLowerCase()}.`} />;
  }
  const innerHeight = HEIGHT - AXIS_GAP;
  const median = points.map((p) => (p.count > 0 ? p.medianMinutes : null));
  const p90 = points.map((p) => (p.count > 0 ? p.p90Minutes : null));
  const max = niceMax(points.map((p) => p.p90Minutes));
  const days = points.map((p) => p.since);
  return (
    <Frame label={label}>
      <path d={bandPath(median, p90, WIDTH, innerHeight, max)} fill={color} fillOpacity={0.14} />
      <path
        d={linePath(median, WIDTH, innerHeight, max)}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <line
        x1={0}
        y1={innerHeight}
        x2={WIDTH}
        y2={innerHeight}
        stroke="var(--dy-line)"
        strokeWidth={1}
      />
      <XAxisLabels days={days} count={points.length} />
    </Frame>
  );
}

/** The queue's size at the end of each day. Zero is a real answer (the queue
 * was empty), so unlike `LeadTimeChart` nothing here is drawn as a gap. */
export function QueueDepthChart({
  points,
  label,
  color,
}: {
  points: readonly DailyCount[];
  label: string;
  color: string;
}) {
  if (!hasCounts(points)) {
    return <ChartEmpty text="Nothing waited in review this window." />;
  }
  const innerHeight = HEIGHT - AXIS_GAP;
  const values = points.map((p) => p.count);
  const max = niceMax(values);
  const days = points.map((p) => p.since);
  const line = linePath(values, WIDTH, innerHeight, max);
  const area = `${line} L${WIDTH},${innerHeight} L0,${innerHeight} Z`;
  return (
    <Frame label={label}>
      <path d={area} fill={color} fillOpacity={0.14} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <line
        x1={0}
        y1={innerHeight}
        x2={WIDTH}
        y2={innerHeight}
        stroke="var(--dy-line)"
        strokeWidth={1}
      />
      <XAxisLabels days={days} count={points.length} />
    </Frame>
  );
}

/** Pickup and decision, the gate's two halves, as two plain lines - no band:
 * the point is comparing them, not either one's own spread. */
export function PickupDecisionChart({
  pickup,
  decision,
  pickupColor,
  decisionColor,
}: {
  pickup: readonly DailyMinutes[];
  decision: readonly DailyMinutes[];
  pickupColor: string;
  decisionColor: string;
}) {
  if (!hasCounts(pickup) && !hasCounts(decision)) {
    return <ChartEmpty text="Nothing picked up or decided yet." />;
  }
  const innerHeight = HEIGHT - AXIS_GAP;
  const pickupValues = pickup.map((p) => (p.count > 0 ? p.medianMinutes : null));
  const decisionValues = decision.map((p) => (p.count > 0 ? p.medianMinutes : null));
  const max = niceMax(
    pickup.map((p) => p.medianMinutes),
    decision.map((p) => p.medianMinutes),
  );
  const days = pickup.map((p) => p.since);
  return (
    <Frame label="Pickup against decision time">
      <path
        d={linePath(pickupValues, WIDTH, innerHeight, max)}
        fill="none"
        stroke={pickupColor}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d={linePath(decisionValues, WIDTH, innerHeight, max)}
        fill="none"
        stroke={decisionColor}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="4 3"
      />
      <line
        x1={0}
        y1={innerHeight}
        x2={WIDTH}
        y2={innerHeight}
        stroke="var(--dy-line)"
        strokeWidth={1}
      />
      <XAxisLabels days={days} count={pickup.length} />
    </Frame>
  );
}
